import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import PedContentCard from '@/components/ped-content/PedContentCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InfinityList from '@/components/ui/custom/InfinityList';
import { Loading } from '@/components/ui/daisy';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import usePedagogicalContents from '@/data/hook/ped-content/usePedagogicalContents';
import { CourseModule } from '@/models/CourseModule';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';

type ModuleCardProps = {
  module: CourseModule;
  courseLineId: number;
  onDelete: (moduleId: number) => void;
  isDeleting?: boolean;
  index: number;
  isReordering: boolean;
  isDraggable?: boolean;
  isDragging?: boolean;
};

export default function ModuleCard({
  module,
  courseLineId,
  onDelete,
  isDeleting,
  index,
  isDraggable,
  isReordering,
  isDragging,
}: ModuleCardProps) {
  const { navigate } = useCourseTreeNavigate();

  const handleEdit = () => {
    navigate(ROUTES.ADMIN.COURSE_LINES.MODULE(courseLineId, module.id), {
      treeKey: mountKey({ type: 'module', id: module.id }),
    });
  };

  const dragProps = isDraggable
    ? {
        isDraggable: true as const,
        id: mountKey({ type: 'module', id: module.id }),
        index,
        disabled: isReordering,
      }
    : { isDraggable: false as const };

  return (
    <CourseAdminCard
      variant="block"
      isDeleting={isDeleting}
      isDragging={isDragging}
      className="mb-6"
      {...dragProps}
    >
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          {module.bannerImg && (
            <CourseAdminCard.Banner
              src={module.bannerImg as string}
              alt={module.name}
            />
          )}
          <CourseAdminCard.Order value={module.order} />
          <CourseAdminCard.Title>{module.name}</CourseAdminCard.Title>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          <CourseAdminCard.EditButton onClick={handleEdit} />
          <CourseAdminCard.MoreActions>
            <CourseAdminCard.MoreActions.Delete
              onClick={() => onDelete(module.id)}
            />
          </CourseAdminCard.MoreActions>
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
      <CourseAdminCard.NestedList>
        <PedContentList moduleId={module.id} courseLineId={courseLineId} />
      </CourseAdminCard.NestedList>
    </CourseAdminCard>
  );
}

type PedContentListProps = {
  moduleId: number;
  courseLineId: number;
};

function PedContentList({ moduleId, courseLineId }: PedContentListProps) {
  const {
    coursePaths,
    deleteCoursePath,
    isDeleting,
    isLoading,
    ...infinityProps
  } = usePedagogicalContents({
    moduleId,
    courseLineId,
  });

  if (!coursePaths.length && !isLoading) return null;

  return (
    <InfinityList {...infinityProps} className="flex flex-col gap-6">
      <ConditionalRenderer condition={isLoading}>
        <div className="flex justify-center py-4">
          <Loading variant="spinner" size="sm" color="primary" />
        </div>
      </ConditionalRenderer>

      {coursePaths.map(path => (
        <PedContentCard
          key={path.slug}
          coursePath={path}
          courseLineId={courseLineId}
          moduleId={moduleId}
          onDelete={deleteCoursePath}
          isDeleting={isDeleting}
        />
      ))}
    </InfinityList>
  );
}
